import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import {
  Pizza, ShoppingCart, FileCheck, CreditCard, Tag, ArrowRight, ArrowLeft, Check,
} from "lucide-react";

/* --- CSS mockups (no real screenshots needed) --- */
const MockOrders = () => (
  <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 text-left">
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs font-bold text-slate-700">OC-2026-0042</span>
      <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">Aprobada</span>
    </div>
    {["Queso mozzarella · 40 kg", "Harina 000 · 100 kg", "Salsa de tomate · 30 L"].map((l, i) => (
      <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
        <span className="text-xs text-slate-600">{l}</span>
        <span className="text-xs font-semibold text-slate-800">✓</span>
      </div>
    ))}
    <div className="mt-3 flex items-center justify-between">
      <span className="text-[11px] text-slate-500">Sucursal Hermosillo</span>
      <span className="text-sm font-extrabold text-[#1A1A1A]">MX$10,614.00</span>
    </div>
  </div>
);

const MockInvoice = () => (
  <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 text-left">
    <div className="text-xs font-bold text-slate-700 mb-3">Subir factura (CFDI)</div>
    <div className="border-2 border-dashed border-slate-300 rounded-lg py-6 flex flex-col items-center gap-1.5">
      <FileCheck className="w-6 h-6 text-slate-400" />
      <span className="text-[11px] text-slate-500">factura_OC-2026-0042.xml</span>
    </div>
    <div className="mt-3 flex items-center gap-2 text-[11px] text-emerald-700 bg-emerald-50 rounded-lg px-2.5 py-1.5">
      <Check className="w-3.5 h-3.5" /> El monto coincide con la orden
    </div>
  </div>
);

const MockPayments = () => (
  <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 text-left space-y-2">
    {[
      ["F-0042", "Pagada", "bg-emerald-100 text-emerald-700"],
      ["F-0051", "En revisión", "bg-amber-100 text-amber-700"],
      ["F-0058", "Programada", "bg-blue-100 text-blue-700"],
    ].map(([f, s, c], i) => (
      <div key={i} className="flex items-center justify-between">
        <span className="text-xs text-slate-600">{f}</span>
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${c}`}>{s}</span>
      </div>
    ))}
  </div>
);

const MockPrices = () => (
  <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 text-left space-y-2">
    {[
      ["Queso mozzarella", "MX$185.00", "MX$199.00"],
      ["Harina 000", "MX$22.50", "MX$24.00"],
    ].map(([p, o, n], i) => (
      <div key={i} className="flex items-center justify-between">
        <span className="text-xs text-slate-600">{p}</span>
        <span className="text-[11px]"><span className="line-through text-slate-400">{o}</span> <span className="font-bold text-[#1A1A1A]">{n}</span></span>
      </div>
    ))}
    <div className="text-[10px] text-slate-500 pt-1">Pendiente de aprobación por Chizy Chiz</div>
  </div>
);

export default function SupplierWelcomeTour() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);

  useEffect(() => {
    api.get("/ops/supplier-welcome-video")
      .then((r) => setVideoUrl(r.data?.video_url || null))
      .catch(() => setVideoUrl(null));
  }, []);

  const shareUrl = videoUrl ? videoUrl.replace("/embed/", "/share/") : null;

  const steps = [
    { icon: Pizza, mock: null },
    { icon: ShoppingCart, mock: <MockOrders /> },
    { icon: FileCheck, mock: <MockInvoice /> },
    { icon: CreditCard, mock: <MockPayments /> },
    { icon: Tag, mock: <MockPrices /> },
  ];

  const finish = async () => {
    setBusy(true);
    try {
      await api.post("/auth/mark-tutorial-seen");
      await refreshUser();
    } catch {
      /* noop */
    }
    navigate("/dashboard", { replace: true });
  };

  const current = steps[step];
  const Icon = current.icon;
  const isFirst = step === 0;
  const isLast = step === steps.length - 1;

  return (
    <div className="fixed inset-0 z-50 bg-[#1A1A1A]/95 flex items-center justify-center p-4" data-testid="supplier-welcome-tour">
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="bg-white rounded-3xl w-full max-w-[600px] p-8 sm:p-10 text-center"
        >
          <div className="mx-auto w-16 h-16 rounded-2xl bg-[#FFDF00] flex items-center justify-center mb-6">
            <Icon className="w-8 h-8 text-[#1A1A1A]" strokeWidth={2.2} />
          </div>

          <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-[#1A1A1A] leading-tight" data-testid="tour-title">
            {t(`tour.step${step + 1}.title`)}
          </h1>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed max-w-md mx-auto">
            {t(`tour.step${step + 1}.body`)}
          </p>

          {isFirst && videoUrl && (
            <div className="mt-6 text-left" data-testid="welcome-video-block">
              <h2 className="font-display font-bold text-base text-[#1A1A1A]">{t("tour.videoTitle")}</h2>
              <p className="text-xs text-slate-500 mb-3">{t("tour.videoSubtitle")}</p>
              <div style={{ position: "relative", paddingBottom: "56.25%", height: 0 }}
                className="rounded-xl overflow-hidden border border-slate-200 bg-slate-100">
                <iframe
                  data-testid="welcome-video-iframe"
                  src={videoUrl}
                  title={t("tour.videoTitle")}
                  frameBorder="0"
                  allowFullScreen
                  style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
                />
              </div>
              {shareUrl && (
                <a data-testid="welcome-video-fallback" href={shareUrl} target="_blank" rel="noopener noreferrer"
                  className="inline-block mt-2 text-[11px] font-semibold text-slate-500 hover:text-[#1A1A1A] underline">
                  {t("tour.videoFallback")}
                </a>
              )}
            </div>
          )}

          {current.mock && <div className="mt-6">{current.mock}</div>}

          {/* progress dots */}
          <div className="flex items-center justify-center gap-1.5 mt-7">
            {steps.map((_, i) => (
              <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-[#1A1A1A]" : "w-1.5 bg-slate-300"}`} />
            ))}
          </div>

          <div className="mt-7 flex items-center justify-between gap-3">
            {isFirst ? (
              <button data-testid="tour-skip" onClick={finish} disabled={busy}
                className="text-xs font-semibold text-slate-400 hover:text-slate-600">
                {t("tour.skip")}
              </button>
            ) : (
              <button data-testid="tour-back" onClick={() => setStep((s) => s - 1)}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-[#1A1A1A]">
                <ArrowLeft className="w-4 h-4" /> {t("tour.back")}
              </button>
            )}

            {isLast ? (
              <button data-testid="tour-finish" onClick={finish} disabled={busy}
                className="inline-flex items-center gap-1.5 px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-bold disabled:opacity-50">
                <Check className="w-4 h-4" /> {t("tour.finish")}
              </button>
            ) : (
              <button data-testid="tour-next" onClick={() => setStep((s) => s + 1)}
                className="inline-flex items-center gap-1.5 px-6 py-2.5 bg-[#1A1A1A] hover:bg-black text-white rounded-xl text-sm font-bold">
                {isFirst ? t("tour.start") : t("tour.next")} <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
